import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  lazy,
  Suspense
} from "react"
import { useLicenseStatus } from "../modules/mp-utils/ready-utils/hooks"
import { Helmet } from "react-helmet"
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation
} from "react-router-dom"
import OAuthContainer from "../modules/login/pages/OAuth"
import FirebaseToken from "../modules/login/pages/FirebaseToken"
import {
  analytics,
  AssessmentSet,
  LicensesSet,
  User
} from "../modules/mp-database"
import { Box, Container } from "@mui/material"
import {
  MainNav,
  PlatformSettingsContext,
  ProgramsContext
} from "../modules/mp-shared-components/ready-shared-components/components/Navs"
import BottomTipsPanel from "../modules/mp-shared-components/ready-shared-components/components/BottomTipsPanel"
import {
  EventAction,
  EventCategory,
  EventLabel,
  Roles
} from "../modules/mp-utils/generic-utils"
import { trackEvent } from "../modules/mp-utils/generic-utils/Events/eventService"
import { logEvent } from "../modules/mp-utils/generic-utils/Events/helpers"

import { useClarityRouteTracking } from "../utils/clarityRouteTracking"
import queryString from "query-string"
import getHomePath from "../modules/mp-utils/ready-utils/getHomePath"
import { consumePostLoginRedirect } from "@masteryprep/mp-shared-components/utils"
import Loader from "../modules/snapcourse/components/Loader"
import { SSO_NAME_MAPPING } from "../modules/login/LoggedOutSSO/constants"
import { useCheckUsersCurrentPageAccess } from "../modules/mp-shared-components/ready-shared-components/components/Navs/MainNav/hooks"
import {
  GoToCurrentVersionBannerContainer,
  MainContentLazyLoader
} from "../modules/mp-core/components"
import { FeatureFlagContext } from "../modules/mp-core/FeatureFlagProvider"
import {
  ImpersonationBannerContainer,
  LeaveImpersonationRouteContainer,
  useLeaveImpersonationManage
} from "../modules/mp-core/Impersonation"
import { useStyles } from "./styles"

////////////////////Auth Routes///////////////////////
export const PracticeTestsMain = lazy(
  () => import("../modules/practice-testing")
) as any

export const StudyHallContainer = lazy(
  () => import("../modules/study-hall/pages/StudyHallContainer")
) as any

export const EoCDashboard = lazy(
  () => import("../modules/eoc/dashboard")
) as any

export const EoCStudyPlans = lazy(
  () => import("../modules/eoc/study-plans")
) as any

export const EoCInsights = lazy(() => import("../modules/eoc/insights")) as any

export const EoCNotFound = lazy(
  () => import("../modules/eoc/pages/EocNotFound")
) as any

export const EocUnavailableInfo = lazy(
  () => import("../modules/eoc/pages/EocUnavailableInfo")
) as any

export const MasteryResourcesContainer = lazy(
  () => import("../modules/mastery-resources/")
) as any

const AnswerDocumentUpload = lazy(
  () =>
    import(
      "../modules/practice-testing/pages/AnswerDocumentUpload/AnswerDocumentUpload"
    )
) as any

export const Profile = lazy(() => import("../modules/profile")) as any

export const EbooksContainer = lazy(() => import("../pages/Ebooks")) as any

/**
 * @deprecated DEPRECATED_MANAGE_ORG_NAV — consumes the old `/manage/organization` user-management nav.
 * Remove or repoint to `/reporting/organization` when that nav is deleted.
 */
export const ManageEducators = lazy(
  // @deprecated DEPRECATED_MANAGE_ORG_NAV — old `/manage/organization` nav usage; remove or repoint to `/reporting/organization` when that nav is deleted.
  () => import("../modules/user-management/pages/ManageUsers/ManageUsers")
) as any

export const Licenses = lazy(
  () => import("../modules/user-management/pages/Licenses/Licenses")
) as any

export const AuthorContentContainer = lazy(
  () => import("../modules/author-tool/pages")
) as any

export const ContextHelpManager = lazy(
  () => import("../modules/user-management/pages/ContextHelpManager")
) as any

export const HelpDesk = lazy(() => import("../modules/helpdesk")) as any

export const SnapReports = lazy(() => import("../modules/snap/Reports")) as any

export const SnapcourseApp = lazy(
  () => import("../modules/snapcourse/App")
) as any

export const Reports = lazy(() => import("../modules/reports")) as any

export const STAARTest = lazy(
  () => import("../modules/eoc/pages/STAARTests")
) as any

export const Reporting = lazy(() => import("../modules/reporting")) as any
export const StudentExperience = lazy(
  () => import("../modules/student-experience")
) as any

// For full-screen test-taking
export const MasteryCurriculumTestTaking = lazy(
  () =>
    import(
      "../modules/reporting/pages/TeachingTools/MasteryResources/pages/TestingContainer/MR_TestingContainer"
    )
) as any

// Teaching Tools full-screen test-taking (same component, different context)
export const TeachingToolsTestTaking = lazy(
  () =>
    import(
      "../modules/reporting/pages/TeachingTools/MasteryResources/pages/TestingContainer/MR_TestingContainer"
    )
) as any

// export const ExternalSettings = lazy(() => import("../modules/mp-core/ExternalSettings"))

/////////// UnAuthRoutes /////////////////
const RequestPasswordReset = lazy(
  () => import("../modules/login/RequestPasswordReset")
) as any

const ResetsContainer = lazy(
  () => import("../modules/login/ResetsContainer")
) as any

const SignInContainer = lazy(
  () => import("../modules/login/SignInContainer")
) as any

const AuthSSO = lazy(() => import("../modules/login/AuthSSO")) as any

const LoggedOutSSO = lazy(() => import("../modules/login/LoggedOutSSO")) as any

const SchoologyLanding = lazy(
  () => import("../modules/login/pages/SchoologyLanding")
) as any

const InvitesContainer = lazy(
  () => import("../modules/login/InvitesContainer")
) as any

const RegisterContainer = lazy(() => import("./Register")) as any

const ContentContainer = lazy(() => import("./Content")) as any

interface RouterProps {
  user: User
  isAuthed: boolean
  isFetching: boolean
  userLicenses: LicensesSet[]
  assesmentSets?: {
    [key: string]: AssessmentSet
  }
}

export enum LicenseNavCategories {
  TruScoreTesting = "TruScore Testing",
  MasteryResources = "Mastery Resources",
  ACTResources = "ACT Resources",
  Resources = "Resources",
  StudyHall = "Study Hall",
  elements = "elements",
  snap = "snap",
  eBooks = "eBooks",
  StudyPlan = "Study Plan"
}

type PageLicenseDataType = {
  navCategory: LicenseNavCategories[]
  program: string
}

type WithRoleAndMainLayoutArgsType = {
  component: React.ReactElement
  roles?: string[]
  pageLicenseData?: PageLicenseDataType
  showFooter?: boolean
  mainComponent?: React.ElementType<any>
}

const isLicenseExpired = (
  user: User,
  userLicenses: LicensesSet[],
  pageLicenseData?: PageLicenseDataType
) => {
  if (!pageLicenseData || !userLicenses) {
    return false
  }

  const { navCategory, program } = pageLicenseData

  const selectedLicense = userLicenses.find(license => {
    return (
      navCategory.some(category =>
        license.navCategory?.toLowerCase().includes(category?.toLowerCase())
      ) && license.navName?.toLowerCase().includes(program?.toLowerCase())
    )
  })

  if (!!selectedLicense) {
    if (
      !["Student", "Super Administrator"].includes(user.role) &&
      navCategory.includes(LicenseNavCategories.elements)
    ) {
      return selectedLicense.endDate < Date.now()
    }

    return !!selectedLicense.isExpired
  }

  return false
}

const notFound = () => {
  return (
    <Box width={1} height={1}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height={"100vh"}
        flexDirection="column"
      >
        <h1>404</h1>
        <p>Not Found</p>
      </Box>
    </Box>
  )
}

const Router: React.FC<RouterProps> = props => {
  const { user, isAuthed, isFetching, userLicenses, assesmentSets } = props
  const {
    currentProgram = null,
    programsAndCourses = [],
    programsAndCoursesLoading = false
  } = useContext(ProgramsContext) || {}

  const { impersonationEnable } = useLeaveImpersonationManage()

  // Use the hook directly to ensure we always have the values
  const { hasOnlyACTLegacyLicenses } = useLicenseStatus(userLicenses)

  const {
    defaultProgram = null,
    defaultProgramLoading: { fetching: defaultProgramLoading = false } = {}
  } = useContext(PlatformSettingsContext) || {}

  const { featureFlags } = useContext(FeatureFlagContext)

  const [redirectPath, setRedirectPath] = useState("")
  const location = useLocation()
  const history = useHistory()

  const { classes } = useStyles()

  // Track route changes in Clarity for SPA navigation
  useClarityRouteTracking(user)

  const logPageViewEvent = logEvent(
    user,
    currentProgram,
    "",
    EventCategory.NAVIGATION,
    EventLabel.REPORTING
  )

  useEffect(() => {
    if (!user || !userLicenses) return

    // A welcome-email link may have stashed a destination page before login
    // (see postLoginRedirect). Honor it once, in place of the normal
    // role-based landing page computed by getHomePath.
    const postLoginRedirect = consumePostLoginRedirect()
    if (postLoginRedirect) {
      setRedirectPath(postLoginRedirect)
      return
    }

    setRedirectPath(
      getHomePath(
        user,
        userLicenses,
        currentProgram,
        featureFlags,
        hasOnlyACTLegacyLicenses
      )
    )
  }, [
    user,
    userLicenses,
    currentProgram,
    featureFlags,
    hasOnlyACTLegacyLicenses
  ])

  useEffect(() => {
    const unlisten = history.listen(async () => {
      logPageViewEvent(EventAction.VIEW, null, null)
      trackEvent(
        {
          type: EventAction.VIEW,
          category: EventCategory.NAVIGATION,
          label: EventLabel.REPORTING,
          program: currentProgram
        },
        "log"
      )
    })
    return () => unlisten()
  }, [history, currentProgram, user])

  useCheckUsersCurrentPageAccess(user, userLicenses, assesmentSets)

  const withRoleAndMainLayout = useCallback(
    ({
      component,
      roles = [],
      pageLicenseData = null,
      showFooter = true,
      mainComponent = "div"
    }: WithRoleAndMainLayoutArgsType) => {
      if (
        (user.role !== Roles.Super_Administrator &&
          !roles.includes(user.role)) ||
        isLicenseExpired(user, userLicenses, pageLicenseData)
      ) {
        return <Redirect to={redirectPath} />
      }

      return (
        <>
          <GoToCurrentVersionBannerContainer userLicenses={userLicenses} />

          <MainNav
            licenses={userLicenses}
            user={user}
            assessmentSets={assesmentSets}
          />
          <Container
            style={{
              padding: 0,
              flexGrow: 1,
              display: "flex",
              flexDirection: "column"
            }}
            maxWidth="xl"
            component={mainComponent}
            tabIndex={mainComponent === "div" ? undefined : 0}
          >
            {component}
          </Container>

          <BottomTipsPanel />
        </>
      )
    },
    [
      userLicenses,
      user,
      assesmentSets,
      redirectPath,
      hasOnlyACTLegacyLicenses,
      history,
      location.pathname
    ]
  )

  return (
    <Box
      display="flex"
      flexDirection="column"
      minHeight="100vh"
      className={classes.appWideBackgroundColor}
    >
      <Helmet>
        <title>MasteryPrep</title>
      </Helmet>

      <ImpersonationBannerContainer
        isAuthed={isAuthed}
        excludeRoutes={[
          "/reporting/*",
          "/student-experience/*",
          "/auth/token",
          "/leave-impersonation"
        ]}
      />

      {isAuthed === true && user && !isFetching ? (
        <Suspense fallback={<MainContentLazyLoader />}>
          <Switch>
            <Route
              path="/:page(truscore)/:module(practice-tests|bubblesheets|study-hall)?/:program?"
              render={props => {
                const { match } = props

                //TODO: Maybe merge study hall with practice_test package
                if (match?.params?.module === "study-hall") {
                  return (
                    <Route
                      path="/:page(truscore)/:module(study-hall)?/:program?/:subPage?/:subcourseId?/:lessonId?"
                      render={props => {
                        analytics.setCurrentScreen("Study Hall")
                        return withRoleAndMainLayout({
                          component: <StudyHallContainer {...props} />,
                          roles: [
                            Roles.Student,
                            Roles.Teacher,
                            Roles.Instructor,
                            Roles.District_Admin,
                            Roles.ACT_Admin,
                            Roles.ACT_Multi_District_Admin,
                            Roles.Sales_Rep,
                            Roles.School_Admin
                          ],
                          pageLicenseData: {
                            navCategory: [LicenseNavCategories.StudyHall],
                            program: match.params.program
                          }
                        })
                      }}
                    />
                  )
                }

                return (
                  <Route
                    path="/:page(truscore)/:module(practice-tests|bubblesheets)?/:program?/:subPage?/:assessmentId?/:subtestId?"
                    render={props => {
                      return withRoleAndMainLayout({
                        component: <PracticeTestsMain {...props} />,
                        roles: [
                          Roles.Student,
                          Roles.Teacher,
                          Roles.Instructor,
                          Roles.Sales_Rep,
                          Roles.District_Admin,
                          Roles.ACT_Admin,
                          Roles.ACT_Multi_District_Admin,
                          Roles.School_Admin
                        ],
                        pageLicenseData: {
                          navCategory: [LicenseNavCategories.TruScoreTesting],
                          program: match.params.program
                        }
                      })
                    }}
                  />
                )
              }}
            />

            <Route
              path="/:page(mastery)/:module(resources)?/:program?/:subPage(lessons|reports|test-taking)?"
              render={props => {
                return withRoleAndMainLayout({
                  component: <MasteryResourcesContainer {...props} />,
                  roles: [
                    Roles.Student,
                    Roles.Teacher,
                    Roles.Instructor,
                    Roles.District_Admin,
                    Roles.ACT_Admin,
                    Roles.ACT_Multi_District_Admin,
                    Roles.Sales_Rep,
                    Roles.School_Admin
                  ],
                  pageLicenseData: {
                    navCategory: [
                      LicenseNavCategories.MasteryResources,
                      LicenseNavCategories.ACTResource,
                      LicenseNavCategories.Resources
                    ],
                    program: props.match.params.program
                  },
                  showFooter: props.match?.params?.subPage !== "test-taking"
                })
              }}
            />

            <Route
              path="/:page(ebooks)/:program?/:ebookId?"
              render={props => {
                analytics.setCurrentScreen("EBooks")
                return withRoleAndMainLayout({
                  component: <EbooksContainer {...props} />,
                  roles: [
                    Roles.Student,
                    Roles.Teacher,
                    Roles.Instructor,
                    Roles.Sales_Rep,
                    Roles.District_Admin,
                    Roles.ACT_Admin,
                    Roles.ACT_Multi_District_Admin,
                    Roles.School_Admin
                  ],
                  pageLicenseData: {
                    navCategory: [LicenseNavCategories.eBooks],
                    program: props.match.params.program
                  }
                })
              }}
            />

            <Route
              path="/manage/platform-configuration"
              render={({ location }) => (
                <Redirect
                  to={
                    location.pathname.replace(
                      "/manage/platform-configuration",
                      "/reporting/configuration"
                    ) +
                    location.search +
                    location.hash
                  }
                />
              )}
            />

            <Route
              path="/:page(manage)/:module(organization|licenses|content)?/:subPage?"
              render={props => {
                const { match } = props
                let currentRoute = <></>

                let roles = [
                  Roles.School_Admin,
                  Roles.District_Admin,
                  Roles.ACT_Admin,
                  Roles.ACT_Multi_District_Admin,
                  Roles.Teacher,
                  Roles.Course_Author
                ]

                let showFooter = true

                // @deprecated DEPRECATED_MANAGE_ORG_NAV — old `/manage/organization` nav usage; remove or repoint to `/reporting/organization` when that nav is deleted.
                if (match?.params?.module === "organization") {
                  currentRoute = (
                    <Route
                      // @deprecated DEPRECATED_MANAGE_ORG_NAV — old `/manage/organization` nav usage; remove or repoint to `/reporting/organization` when that nav is deleted.
                      path="/:page(manage)/:module(organization)/:subPage"
                      render={props => {
                        analytics.setCurrentScreen("Manage Educators")
                        // @deprecated DEPRECATED_MANAGE_ORG_NAV — old `/manage/organization` nav usage; remove or repoint to `/reporting/organization` when that nav is deleted.
                        return <ManageEducators {...props} />
                      }}
                    />
                  )
                  roles = [
                    Roles.School_Admin,
                    Roles.District_Admin,
                    Roles.ACT_Admin,
                    Roles.ACT_Multi_District_Admin,
                    Roles.Teacher
                  ]
                  showFooter = false
                }
                if (match?.params?.module === "licenses") {
                  currentRoute = (
                    <Route
                      path="/:page(manage)/:module(licenses)/:subPage"
                      render={props => {
                        analytics.setCurrentScreen("Licenses")
                        return <Licenses {...props} />
                      }}
                    />
                  )
                  if (match?.params?.subPage === "sitelicenses") {
                    roles = [Roles.Super_Administrator]
                  }
                }
                if (match?.params?.module === "content") {
                  currentRoute = (
                    <Route
                      path="/:page(manage)/:module(content)/:subPage"
                      render={props => {
                        analytics.setCurrentScreen("Content Manager")
                        return <AuthorContentContainer {...props} />
                      }}
                    />
                  )
                  roles = [Roles.Course_Author]
                }

                return withRoleAndMainLayout({
                  component: currentRoute,
                  roles,
                  showFooter
                })
              }}
            />

            <Route
              path="/:page(reports)/:module(usage-report|participation-report)"
              render={props => {
                analytics.setCurrentScreen("Reports")
                return withRoleAndMainLayout({
                  component: <Reports {...props} />,
                  roles: [
                    Roles.School_Admin,
                    Roles.Sales_Rep,
                    Roles.District_Admin,
                    Roles.ACT_Admin,
                    Roles.ACT_Multi_District_Admin
                  ],
                  showFooter: true,
                  mainComponent: "main"
                })
              }}
            />

            <Route
              path="/:page(school-support)/:module(test-administration)?/:program?/:subPage(answer-document-upload)?/:assessmentId?"
              render={props => {
                analytics.setCurrentScreen("Answer document upload")

                return withRoleAndMainLayout({
                  component: <AnswerDocumentUpload {...props} />
                })
              }}
            />

            <Route
              path="/:page(snap)/:module(reports)/:program?/:subject?"
              render={props => {
                analytics.setCurrentScreen("Snap Reports")
                //@ts-ignore
                return withRoleAndMainLayout({
                  component: <SnapReports {...props} user={user} />,
                  roles: [
                    Roles.Teacher,
                    Roles.Instructor,
                    Roles.School_Admin,
                    Roles.District_Admin,
                    Roles.ACT_Admin,
                    Roles.ACT_Multi_District_Admin
                  ],
                  pageLicenseData: {
                    navCategory: [LicenseNavCategories.snap],
                    program: props.match.params.program
                  }
                })
              }}
            />

            <Route
              path="/:page(snap)/:module(student|elements|demo)/:program?/:subPage?/:contentId?/:snapId?"
              render={props => {
                // analytics.setCurrentScreen("Snap Course")
                return withRoleAndMainLayout({
                  component: <SnapcourseApp {...props} />,
                  roles: [
                    Roles.Student,
                    Roles.Teacher,
                    Roles.Instructor,
                    Roles.Course_Author,
                    Roles.District_Admin,
                    Roles.ACT_Admin,
                    Roles.ACT_Multi_District_Admin,
                    Roles.School_Admin,
                    Roles.Super_Administrator
                  ],
                  pageLicenseData: {
                    navCategory: [
                      LicenseNavCategories.snap,
                      LicenseNavCategories.elements
                    ],
                    program: props.match.params.program
                  },
                  showFooter: false
                })
              }}
            />

            <Route
              path="/snap/:page(author)/:courseId?/:topic?"
              render={props => {
                // analytics.setCurrentScreen("Snap Course")
                return withRoleAndMainLayout({
                  component: <SnapcourseApp {...props} />,
                  roles: [Roles.Course_Author, Roles.Super_Administrator],
                  showFooter: false
                })
              }}
            />

            <Route
              path="/snap/:page(teacher)/:module/:program?/:subPage?"
              render={props => {
                // analytics.setCurrentScreen("Snap Course")
                return withRoleAndMainLayout({
                  component: <SnapcourseApp {...props} />,
                  roles: [
                    Roles.Teacher,
                    Roles.Course_Author,
                    Roles.District_Admin,
                    Roles.ACT_Admin,
                    Roles.ACT_Multi_District_Admin,
                    Roles.School_Admin,
                    Roles.Super_Administrator
                  ],
                  pageLicenseData: {
                    navCategory: [
                      LicenseNavCategories.snap,
                      LicenseNavCategories.elements
                    ],
                    program: props.match.params.program
                  },
                  showFooter: false
                })
              }}
            />

            <Route
              path="/:page(eoc)"
              render={() => {
                const availableStudyPlanLicenses = userLicenses?.filter(
                  license =>
                    license.navCategory === "Study Plan" && !license.isExpired
                )

                if (!availableStudyPlanLicenses?.length) {
                  return withRoleAndMainLayout({
                    component: (
                      <EocUnavailableInfo
                        userRole={user.role}
                        infoType="restricted"
                      />
                    ),
                    roles: Object.values(Roles),
                    showFooter: false
                  })
                }

                if (user.role === "Super Administrator") {
                  return withRoleAndMainLayout({
                    component: (
                      <EocUnavailableInfo
                        userRole={user.role}
                        infoType="notSupported"
                      />
                    ),
                    roles: [Roles.Super_Administrator],
                    showFooter: false
                  })
                }

                if (programsAndCoursesLoading || defaultProgramLoading) {
                  return <Loader />
                }

                return (
                  <Switch>
                    <Route
                      path="/:page(eoc)/:program?/:module?/:subject?/:subPage?/:planType?"
                      exact
                      render={props => {
                        const isValidStudyPlanLicense =
                          programsAndCourses.find(
                            programCourse =>
                              programCourse.program.toLowerCase() ===
                              props.match.params?.program
                          ) ||
                          availableStudyPlanLicenses.find(
                            license =>
                              license.navName.split(" ")?.[0]?.toLowerCase() ===
                              props.match.params?.program
                          )

                        // If program in URL is missing or not from the list of available programs,
                        // redirect to the first available program.
                        if (!isValidStudyPlanLicense) {
                          // First Study Plan program by default
                          let firstAvailableProgram: string =
                            programsAndCourses.find(
                              programCourse =>
                                programCourse.programInfo.supportStudyPlan
                            )?.program ||
                            availableStudyPlanLicenses[0].navName
                              .split(" ")?.[0]
                              ?.toLowerCase()

                          // Additional checks required if there is a default program.
                          if (defaultProgram) {
                            const defaultProgramCourse =
                              programsAndCourses.find(
                                programCourse =>
                                  programCourse.program.toLowerCase() ===
                                  defaultProgram.toLowerCase()
                              )

                            const isStudyPlanProgram =
                              defaultProgramCourse?.programInfo.supportStudyPlan
                            // If default program supports Study Plan, redirect to it.
                            // Otherwise, keep first Study Plan program.
                            firstAvailableProgram = isStudyPlanProgram
                              ? defaultProgram
                              : firstAvailableProgram
                          }

                          if (!firstAvailableProgram) {
                            return <Redirect to={`/profile`} />
                          }

                          return (
                            <Redirect
                              to={`/eoc/${firstAvailableProgram.toLowerCase()}/dashboard`}
                            />
                          )
                        }

                        return (
                          <Switch>
                            <Route
                              path="/:page(eoc)/:program?"
                              exact
                              render={props => {
                                return (
                                  <Redirect
                                    to={`/eoc/${props.match.params.program}/dashboard`}
                                  />
                                )
                              }}
                            />

                            <Route
                              path="/:page(eoc)/:program/:module(dashboard)/:subject?"
                              render={props => {
                                return withRoleAndMainLayout({
                                  component: <EoCDashboard {...props} />,
                                  roles: [
                                    Roles.District_Admin,
                                    Roles.ACT_Admin,
                                    Roles.ACT_Multi_District_Admin,
                                    Roles.School_Admin,
                                    Roles.Teacher,
                                    Roles.Student
                                  ],
                                  showFooter: false,
                                  mainComponent: "main"
                                })
                              }}
                            />

                            <Route
                              path="/:page(eoc)/:program/:module(study-plans)/:subject?/:subpage?/:planType?"
                              render={props => {
                                return withRoleAndMainLayout({
                                  component: <EoCStudyPlans {...props} />,
                                  roles: [
                                    Roles.District_Admin,
                                    Roles.ACT_Admin,
                                    Roles.ACT_Multi_District_Admin,
                                    Roles.School_Admin,
                                    Roles.Teacher
                                  ],
                                  showFooter: false
                                })
                              }}
                            />

                            <Route
                              path="/:page(eoc)/:program/:module(insights)/:subject?/:subPage?/:contentId?"
                              render={props => {
                                return withRoleAndMainLayout({
                                  component: <EoCInsights {...props} />,
                                  roles: [
                                    Roles.District_Admin,
                                    Roles.ACT_Admin,
                                    Roles.ACT_Multi_District_Admin,
                                    Roles.School_Admin,
                                    Roles.Teacher,
                                    Roles.Student
                                  ],
                                  showFooter: false
                                })
                              }}
                            />

                            <Route
                              path="/:page(eoc)/:program(staar)/:module(practice-tests)"
                              render={props => {
                                return withRoleAndMainLayout({
                                  component: <STAARTest {...props} />,
                                  roles: [Roles.Student],
                                  showFooter: false
                                })
                              }}
                            />

                            <Route path="*">
                              {withRoleAndMainLayout({
                                component: <EoCNotFound />,
                                roles: Object.values(Roles),
                                showFooter: false
                              })}
                            </Route>
                          </Switch>
                        )
                      }}
                    />
                  </Switch>
                )
              }}
            />

            <Route
              path="/:page(profile)/:subPage(whitelisting)?"
              render={props => {
                analytics.setCurrentScreen("Profile")
                return withRoleAndMainLayout({
                  component: <Profile {...props} />,
                  roles: [
                    Roles.Student,
                    Roles.Teacher,
                    Roles.Instructor,
                    Roles.Sales_Rep,
                    Roles.District_Admin,
                    Roles.ACT_Admin,
                    Roles.ACT_Multi_District_Admin,
                    Roles.School_Admin,
                    Roles.Course_Author
                  ],
                  showFooter: true,
                  mainComponent: "main"
                })
              }}
            />

            <Route
              path="/:page(context-help-manager)/:module(announcements|tips|help-boxes)?"
              render={props => {
                analytics.setCurrentScreen("Context Help Manager")
                return withRoleAndMainLayout({
                  component: <ContextHelpManager {...props} />,
                  roles: [],
                  showFooter: false
                })
              }}
            />

            {/* Full-screen test-taking route for Teaching Tools */}
            <Route
              path="/reporting/teaching-tools/mastery-curriculum/:program(act|eact|sat|psat|dsat|staar|tsia|tsia2)/test-taking/:courseId/:subcourseId/:lessonId"
              render={props => {
                if (
                  ![
                    Roles.Teacher,
                    Roles.School_Admin,
                    Roles.District_Admin,
                    Roles.ACT_Admin,
                    Roles.ACT_Multi_District_Admin,
                    Roles.Super_Administrator
                  ].includes(user.role)
                ) {
                  return <Redirect to={redirectPath} />
                }

                analytics.setCurrentScreen(
                  "Mastery Resources Test Taking - Teaching Tools"
                )
                return (
                  <Suspense fallback={<MainContentLazyLoader />}>
                    <TeachingToolsTestTaking {...props} />
                  </Suspense>
                )
              }}
            />

            <Route
              path="/:page(reporting)/:module?/:subpage?/:entityId?"
              render={() => {
                if (
                  ![
                    Roles.Teacher,
                    Roles.School_Admin,
                    Roles.District_Admin,
                    Roles.ACT_Admin,
                    Roles.ACT_Multi_District_Admin,
                    Roles.Super_Administrator
                  ].includes(user.role)
                ) {
                  return <Redirect to={redirectPath} />
                }

                return <Reporting redirectPath={redirectPath} />
              }}
            />

            {/* Full-screen test-taking route for Mastery Curriculum */}
            <Route
              path="/student-experience/mastery-curriculum/:program(act|eact|sat|psat|dsat|staar|tsia|tsia2)/test-taking/:courseId/:subcourseId/:lessonId"
              render={props => {
                if (![Roles.Student].includes(user.role)) {
                  return <Redirect to={redirectPath} />
                }

                analytics.setCurrentScreen(
                  "Mastery Resources Test Taking - Student Experience"
                )
                return (
                  <Suspense fallback={<MainContentLazyLoader />}>
                    <MasteryCurriculumTestTaking {...props} />
                  </Suspense>
                )
              }}
            />

            <Route
              path="/:page(student-experience)/:module?/:subpage?/:entityId?"
              render={() => {
                if (![Roles.Student].includes(user.role)) {
                  return <Redirect to={redirectPath} />
                }

                return <StudentExperience redirectPath={redirectPath} />
              }}
            />

            <Route
              path="/:page(help)"
              render={props => {
                analytics.setCurrentScreen("Help")
                return withRoleAndMainLayout({
                  component: <HelpDesk {...props} />,
                  roles: [
                    Roles.Student,
                    Roles.Teacher,
                    Roles.Instructor,
                    Roles.District_Admin,
                    Roles.ACT_Admin,
                    Roles.ACT_Multi_District_Admin,
                    Roles.Sales_Rep,
                    Roles.School_Admin,
                    Roles.Course_Author
                  ],
                  showFooter: true,
                  mainComponent: "main"
                })
              }}
            />

            <Route
              path="/:page(leave-impersonation)"
              render={() => (
                <LeaveImpersonationRouteContainer redirectPath={redirectPath} />
              )}
            />

            {redirectPath && (
              <Route
                path=""
                render={props => {
                  return <Redirect to={redirectPath} />
                }}
              />
            )}
          </Switch>
        </Suspense>
      ) : isAuthed !== true ? (
        <Suspense fallback={<MainContentLazyLoader />}>
          <Switch>
            <Route
              exact
              path={["/", "/:page(login)", "/signin-username", "/login/saml"]}
              render={props => <SignInContainer {...props} />}
            />
            <Route
              exact
              path="/schoology"
              render={props => <SchoologyLanding {...props} />}
            />
            <Route
              path="/oauth/:provider"
              //@ts-ignore
              render={props => <OAuthContainer {...props} />}
            />
            <Route
              path="/auth/token"
              //@ts-ignore
              render={props => <FirebaseToken {...props} />}
            />
            <Route
              exact
              path="/auth/sso"
              render={props => <AuthSSO {...props} />}
            />
            <Route
              exact
              path="/logged-out/:sso?"
              render={props => {
                if (!SSO_NAME_MAPPING[props.match.params.sso]) {
                  return <Redirect to="/" />
                }

                return <LoggedOutSSO {...props} />
              }}
            />
            <Route
              exact
              path="/reset/:resetId"
              render={props => {
                if (props.match.params.resetId === "request-reset") {
                  return <RequestPasswordReset {...props} />
                } else if (props.match.params.resetId === "expired") {
                  const query = queryString.parse(props.location.search)
                  return (
                    <RequestPasswordReset
                      {...props}
                      expired={{ email: query.e }}
                    />
                  )
                }
                return <ResetsContainer {...props} />
              }}
            />
            <Route
              exact
              path="/invites/:inviteId"
              render={props => <InvitesContainer {...props} />}
            />
          </Switch>
        </Suspense>
      ) : null}
    </Box>
  )
}

export default Router
